/**
 * Paleta de colores del proyecto
 * Definición centralizada de todos los colores utilizados en la aplicación
 */

export const colors = {
  // Feedback
  feedback: {
    success100: '#F9FFE5',     // Success 100
    success200_2: '#D3E4A0',   // Success 200_2
    success200: '#C8DE8C',     // Success 200
    success300: '#ACCA54',     // Success 300
    success400: '#6C7E01',     // Success 400
    alert100: '#FFF8E5',       // Alert 100
    alert300: '#FFB800',       // Alert 300
    alert400: '#B77900',       // Alert 400
    error100: '#FDDBDB',       // Error 100
    error300: '#E24C4C',       // Error 300
    error400: '#E12C2C',       // Error400  
    error500: '#A2263B',       // Error500
  },

  // Primary
  primary: {
    main: '#1E5591',           // Primary
    dark: '#0A396C',           // Primary dark
    dash: '#5780AD',           // Primary Dash
    general: '#B2E5FF',        // Primary General
    auxiliar: '#B6CEE7',       // Primary Auxiliar
    background200: '#E2ECF6',  // Background 200
    background100: '#ECF2F9',  // Background 100
    background: '#F6F9FA',     // Background
    background2: '#F0F5F9',
  },

  // Important
  important: {
    main: '#00B4FF',           // Important
    dark: '#019BE5',           // Important dark
    intermediate: '#0069FF',   // Important intermediate
  },

  // Grays
  grays: {
    neutralFF: '#FFFFFF',      // Neutral FF
    neutralFA: '#FAFAFA',      // Neutral FA
    neutralEE: '#EEEEEE',      // Neutral EE
    neutralE5: '#E5E5E5',      // Neutral E5
    neutralCC: '#CCCCCC',      // Neutral CC
    neutral99: '#999999',      // Neutral 99
    neutral66: '#666666',      // Neutral 66
    neutral33: '#333333',      // Neutral 33
    neutral00: '#000000',      // Neutral 00
    overlayBackdrop: '#FFFFFF52', // Neutral FF @ 32% opacity — used for modal/dialog/panel backdrops
  },

  notification: {
    successBackground: '#F7FCDE',
    successIconContainer: '#9FB853',
    errorBackground: '#FDDBDB',           // mismo que feedback.error100
    errorIconContainer: '#E24C4C',        // mismo que feedback.error300
    alertBackground: '#FFF6CC',
    alertIconContainer: '#FFB800',        // mismo que feedback.alert300
    importantBackground: '#E1F2FA',
    importantIconContainer: '#00B4FF',    // mismo que important.main
    primaryBackground: '#E1F2FA',         // mantiene mismo fondo que importantBackground
    primaryIconContainer: '#1E5591',      // mismo que primary.main
  },

  // Export formats
  export: {
    excel: {
      main: '#4CAF50',
      hover: 'rgba(76, 175, 80, 0.08)',
      pressed: 'rgba(76, 175, 80, 0.15)',
    },
    pdf: {
      main: '#FF6B6B',
      hover: 'rgba(255, 107, 107, 0.08)',
      pressed: 'rgba(255, 107, 107, 0.15)',
    },
    word: {
      main: '#5E9FD6',
      hover: 'rgba(94, 159, 214, 0.08)',
      pressed: 'rgba(94, 159, 214, 0.15)',
    },
  },

  // Dashboard attendance status backgrounds
  dashboardStatus: {
    presenteBg:    'rgba(172, 202, 84, 0.20)',
    ausenteBg:     'rgba(226, 76, 76, 0.20)',
    advertenciaBg: 'rgba(255, 184, 0, 0.20)',
    ausentismoBg:  'rgba(0, 180, 255, 0.20)',
    sinMarcajeBg:  'rgba(153, 153, 153, 0.20)',
    remoto:        '#00D4B4',  // Teal — marcajes remotos en el dashboard mensual
  },

} as const;

/**
 * Mapeo de colores por contexto de uso
 * Facilita el uso semántico de los colores
 */
export const semanticColors = {
  // Textos
  text: {
    primary: colors.primary.dark,        // #0A396C
    secondary: colors.grays.neutral33,   // #333333
    muted: colors.grays.neutral99,       // #999999
    light: colors.grays.neutral66,       // #666666
    inverse: colors.grays.neutralFF,     // #FFFFFF
  },

  // Fondos
  background: {
    primary: colors.grays.neutralFF,     // #FFFFFF
    secondary: colors.primary.background, // #F6F9FA
    tertiary: colors.primary.auxiliar,   // #B6CEE7
    dark: colors.primary.main,           // #1E5591
  },

  // Bordes
  border: {
    light: colors.grays.neutralE5,       // #E5E5E5
    medium: colors.grays.neutralCC,      // #CCCCCC
    dark: colors.grays.neutral99,        // #999999
  },

  // Estados
  status: {
    success: colors.feedback.success300,        // #ACCA54
    'success-dark': colors.feedback.success400, // #6C7E01
    warning: colors.feedback.alert300,          // #FFB800
    error: colors.feedback.error300,            // #E24C4C
    info: colors.important.main,                // #00B4FF
    'info-dark': colors.important.dark,         // #019BE5
  },

  // Acciones/Interacciones
  interactive: {
    primary: colors.primary.main,         // #1E5591
    'primary-hover': colors.primary.dark, // #0A396C
    secondary: colors.important.main,     // #00B4FF
    'secondary-hover': colors.important.dark, // #019BE5
  },
} as const;

/**
 * Type helpers para autocompletado
 */
export type ColorKey = keyof typeof colors;
export type SemanticColorKey = keyof typeof semanticColors;
